import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WidgetDataResult, WidgetChartConfig, WidgetChartType } from '../types';
import { ChartBar } from '../WidgetRenderer/ChartBar';
import { ChartLine } from '../WidgetRenderer/ChartLine';
import { ChartPie } from '../WidgetRenderer/ChartPie';
import { ChartTable } from '../WidgetRenderer/ChartTable';
import { ChartMetric } from '../WidgetRenderer/ChartMetric';
import { ChartCardGrid } from '../WidgetRenderer/ChartCardGrid';
import { ChartOpinions } from '../WidgetRenderer/ChartOpinions';
import { ChartWordCloud } from '../WidgetRenderer/ChartWordCloud';
import styles from './DataPreview.module.scss';

/** Build a claude.ai URL with a pre-filled prompt about the SQL error. */
function buildClaudeHelpUrl(sql: string, error: string): string {
  const prompt = [
    'I have a PostgreSQL query that produces an error. Help me understand and fix it.\n',
    '**Query:**',
    '```sql',
    sql.trim(),
    '```\n',
    '**Error:**',
    '```',
    error.trim(),
    '```\n',
    'Explain what causes this error and suggest a corrected query.',
  ].join('\n');
  return `https://claude.ai/new?q=${encodeURIComponent(prompt)}`;
}

interface DataPreviewProps {
  data: WidgetDataResult | null;
  chartType: WidgetChartType;
  config: WidgetChartConfig;
  querySql?: string;
}

export const DataPreview: React.FC<DataPreviewProps> = ({
  data,
  chartType,
  config,
  querySql,
}) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<'chart' | 'table' | 'both'>('both');

  if (!data) {
    return (
      <div className={styles.empty}>
        {t('Run a query to see results here')}
      </div>
    );
  }

  if (data.error) {
    return (
      <div className={styles.errorWrapper}>
        <span className={styles.errorIcon}>⚠</span>
        <p className={styles.errorMessage}>{t('Query returned an error')}</p>
        <pre className={styles.errorDetails}>{data.error}</pre>
        {querySql && (
          <a
            href={buildClaudeHelpUrl(querySql, data.error)}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.helpLink}
          >
            {t('Ask Claude to help fix this')} &rarr;
          </a>
        )}
      </div>
    );
  }

  if (data.rows.length === 0) {
    return <div className={styles.empty}>{t('Query returned no rows')}</div>;
  }

  const renderChart = () => {
    switch (chartType) {
      case 'bar':
        return <ChartBar data={data} config={config} />;
      case 'line':
        return <ChartLine data={data} config={config} />;
      case 'pie':
        return <ChartPie data={data} config={config} />;
      case 'table':
        return <ChartTable data={data} config={config} />;
      case 'metric':
        return <ChartMetric data={data} config={config} />;
      case 'card_grid':
        return <ChartCardGrid data={data} config={config} />;
      case 'opinions':
        return <ChartOpinions data={data} config={config} />;
      case 'word_cloud':
        return <ChartWordCloud data={data} config={config} />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.meta}>
          <span>
            {data.columns.length} {t('columns')}, {data.rows.length} {t('rows')}
          </span>
          {data.executionTimeMs !== undefined && (
            <span className={styles.timing}>{data.executionTimeMs}ms</span>
          )}
        </div>
        <div className={styles.viewToggle}>
          <button
            className={`${styles.toggleBtn} ${viewMode === 'chart' ? styles.active : ''}`}
            onClick={() => setViewMode('chart')}
          >
            {t('Chart')}
          </button>
          <button
            className={`${styles.toggleBtn} ${viewMode === 'table' ? styles.active : ''}`}
            onClick={() => setViewMode('table')}
          >
            {t('Table')}
          </button>
          <button
            className={`${styles.toggleBtn} ${viewMode === 'both' ? styles.active : ''}`}
            onClick={() => setViewMode('both')}
          >
            {t('Both')}
          </button>
        </div>
      </div>

      {(viewMode === 'chart' || viewMode === 'both') && (
        <div className={styles.chartContainer}>
          {renderChart()}
        </div>
      )}

      {(viewMode === 'table' || viewMode === 'both') && (
        <div className={styles.tableContainer}>
          <ChartTable data={data} config={config} />
        </div>
      )}
    </div>
  );
};
